import { Close } from "@mui/icons-material";
import {
  Box,
  Modal,
  Button,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  InputBase,
  Typography,
  IconButton,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import OtpInput from "react-otp-input";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import API from "../api";

const ApplyModal = ({
  open,
  closeHandler,
  getJobsHandler,
  addDummyApplicants,
}) => {
  const [responseError, setResponseError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [errorOpen, setErrorOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [states, setStates] = useState([]);
  const fileInputRef = useRef(null);
  const [otp, setOtp] = useState("");
  const [showOTPField, setShowOTPField] = useState(null);
  const [timeLeft, setTimeLeft] = useState(360);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    highestQualification: "",
    yearsOfExperience: "",
    state: "",
    location: "",
    resume: null,
    coverLetter: "",
  });

  const [errors, setErrors] = useState({
    fullName: "",
    email: "",
    phone: "",
    highestQualification: "",
    yearsOfExperience: "",
    resume: "",
    state: "",
    location: "",
  });

  const validateName = (name) => {
    if (!name.trim()) return "Name is required";
    if (name.length > 255) return "Name must not exceed 255 characters";
    return "";
  };

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required";
    if (email.length > 255) return "Email must not exceed 255 characters";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Invalid email format";
    return "";
  };

  const validatePhone = (phone) => {
    if (!phone) return "Phone number is required";
    if (phone.length > 20) return "Phone number must not exceed 20 characters";
    return "";
  };

  const validateResume = (file) => {
    if (!file) return "Resume is required";
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
    ];
    if (!allowedTypes.includes(file.type)) {
      return "Resume must be PDF, DOC, DOCX, or JPEG";
    }
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      return "Resume must not exceed 5MB";
    }
    return "";
  };

  const onCloseHandler = () => {
    closeHandler();
    setOtp("");
    setIsLoading(false);
    setShowOTPField(null);
    setTimeLeft(360);
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      highestQualification: "",
      yearsOfExperience: "",
      resume: null,
      coverLetter: "",
      state: "",
      location: "",
    });
    setErrors({
      fullName: "",
      email: "",
      phone: "",
      highestQualification: "",
      yearsOfExperience: "",
      resume: "",
      state: "",
      location: "",
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleInputChange = (e) => {
    setResponseError(null);
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      [name]:
        name === "state"
          ? value === ""
            ? "State is required"
            : ""
          : name === "location"
          ? value === ""
            ? "Location is required"
            : ""
          : name === "fullName"
          ? validateName(value)
          : name === "email"
          ? validateEmail(value)
          : name === "highestQualification"
          ? value === ""
            ? "Qualification is required"
            : ""
          : name === "yearsOfExperience"
          ? value === ""
            ? "Experience is required"
            : ""
          : "",
    }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prevState) => ({
      ...prevState,
      phone: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      phone: validatePhone(value),
    }));
  };

  const handleFileChange = (e) => {
    setResponseError(null);
    const file = e.target.files[0];
    setFormData((prevState) => ({
      ...prevState,
      resume: file,
    }));

    setErrors((prevState) => ({
      ...prevState,
      resume: validateResume(file),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponseError(null);

    const newErrors = {
      fullName: validateName(formData.fullName),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      highestQualification:
        formData.highestQualification.trim() === ""
          ? "Qualification is required"
          : "",
      yearsOfExperience:
        formData.yearsOfExperience.trim() === ""
          ? "Experience is required"
          : "",
      resume: validateResume(formData.resume),
      state: formData.state === "" ? "State is required" : "",
      location: formData.location === "" ? "Location is required" : "",
    };

    setErrors(newErrors);

    const isFormValid = Object.values(newErrors).every((error) => !error);

    if (isFormValid) {
      setIsLoading(true);
      try {
        const formObj = new FormData();
        formObj.append("name", formData.fullName.trim());
        formObj.append("email", formData.email.trim());
        formObj.append("phone_number", formData.phone);
        formObj.append("qualification", formData.highestQualification);
        formObj.append("experience", formData.yearsOfExperience);
        formObj.append("resume", formData.resume);
        formObj.append("cover_letter", formData.coverLetter);
        formObj.append("state", formData.state);
        formObj.append("location", formData.location);
        formObj.append("job_id", open);
        const resp = await API.post("/api/apply-against-job", formObj);
        if (resp?.data?.success) {
          if (getJobsHandler) {
            getJobsHandler();
          }
          if (addDummyApplicants) {
            addDummyApplicants(formData.fullName);
          }
          setIsLoading(false);
          if (resp?.data?.data?.otp_sent) {
            setShowOTPField(resp?.data?.data?.applicant?.id);
            setTimeLeft(360);
          }
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        } else {
          setIsLoading(false);
          setResponseError(resp?.data?.msg);
          setErrorOpen(true);
        }
      } catch (error) {
        setIsLoading(false);
        setResponseError(error?.response?.data?.message);
        setErrorOpen(true);
        console.log(error);
      }
    }
  };

  const verifyOtp = async () => {
    setIsLoading(true);
    try {
      const resp = await API.post(`/api/verify-otp-for-posted-job-applicant`, {
        applicant_id: showOTPField,
        otp: otp,
      });
      if (resp?.data?.success) {
        setIsLoading(false);
        setSnackbarMessage("Application successfully submitted!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setOtp("");
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          highestQualification: "",
          yearsOfExperience: "",
          resume: null,
          coverLetter: "",
          state: "",
          location: "",
        });
        setShowOTPField(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        setResponseError(resp?.data?.msg);
        setIsLoading(false);
        setErrorOpen(true);
      }
    } catch (error) {
      setIsLoading(false);
      setResponseError(error?.response?.data?.message);
      setErrorOpen(true);
      console.log(error);
    }
  };

  const resendOtp = async () => {
    setOtp("");
    try {
      const resp = await API.get(
        `/api/send-otp-to-posted-job-applicant/${showOTPField}`
      );
      if (resp?.data?.success) {
        setSnackbarMessage(
          "A OTP has been sent to your email. Please check your inbox and enter the OTP to proceed."
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getLocations = async () => {
    try {
      const resp = await API.get("/api/get-states/231");
      if (resp?.data?.success) {
        setStates(resp?.data?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getLocations();
  }, []);

  useEffect(() => {
    if (showOTPField && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showOTPField, timeLeft]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0"
    )}`;
  };

  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            )
              .then((response) => response.json())
              .then((data) => {
                setCountry(data.countryCode);
                setLoading(false);
              })
              .catch((err) => {
                setError("Failed to fetch country data");
                setLoading(false);
              });
          },
          (error) => {
            setError("Failed to get location");
            setLoading(false);
          }
        );
      } else {
        setError("Geolocation is not supported by this browser.");
        setLoading(false);
      }
    };

    getLocation();
  }, []);

  return (
    <Modal open={open} onClose={onCloseHandler}>
      <Box
        sx={{
          display: "flex",
          width: { xs: "95vw", md: "90vw", lg: "80vw" },
          height: { xs: "90vh", md: "90vh" },
          maxHeight: "980px",
          margin: "auto",
          my: { xs: 2, md: 5 },
          mb: { xs: 3, md: "auto" },
          bgcolor: "white",
          borderRadius: "8px",
          boxShadow: 24,
          overflow: "hidden",
        }}
      >
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => {
            setSnackbarOpen(false);
          }}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Box
          sx={{
            width: "50% !important",
            bgcolor: "#F0F4F8 !important",
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Box
            component={"img"}
            src={`https://projects.tangiblethemes.com/Shglocums/wp-content/uploads/2024/12/banner-background.jpg`}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <Box
            sx={{
              p: 2,
              textAlign: "left",
              position: "absolute",
              left: 0,
              bottom: 0,
              width: "100%",
              backgroundImage:
                "linear-gradient(to bottom, transparent, #21404B99)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "24px", xl: "36px" },
                fontWeight: { xs: 500, xl: 600 },
                lineHeight: { xs: "24px", xl: "44px" },
                color: "white",
                mt: 3,
                mb: "24px",
                maxWidth: "417px",
              }}
            >
              Find your Job today <br /> within minutes.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: { xs: 400, xl: 500 },
                fontSize: { xs: "1rem", xl: "20px" },
                lineHeight: { xs: "24px", xl: "30px" },
                color: "white",
              }}
            >
              Email: support@shglocums.com
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: { xs: 400, xl: 500 },
                fontSize: { xs: "1rem", xl: "20px" },
                lineHeight: { xs: "24px", xl: "30px" },
                color: "white",
              }}
            >
              Phone: (123) 456-7890
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: { xs: 400, xl: 500 },
                fontSize: { xs: "1rem", xl: "20px" },
                lineHeight: { xs: "24px", xl: "30px" },
                color: "white",
              }}
            >
              Office location: 123 Main Street, Suite 456, New York, NY 10001
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {errorOpen && (
            <Alert
              onClose={() => setErrorOpen(false)}
              severity={"error"}
              sx={{ width: "100%" }}
            >
              {responseError}
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "1.5rem", xl: "40px" },
                fontFamily: "DM Serif Text",
                letterSpacing: "0.7px",
                color: "#002357",
              }}
            >
              {showOTPField ? "Verify Email" : "Apply now"}
            </Typography>
            <IconButton
              onClick={onCloseHandler}
              sx={{
                "&:hover": {
                  color: "#002357 !important",
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <Box
            className="thin_slider_show"
            sx={{
              flex: 1,
              overflowY: "auto",
              px: { xs: 2, xl: 4 },
              py: 2,
            }}
          >
            <form>
              {!showOTPField && (
                <>
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: { xs: 500 },
                        fontSize: "14px",
                        lineHeight: "24px",
                        color: "#292A2F",
                      }}
                    >
                      Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <InputBase
                      fullWidth
                      placeholder="Full name"
                      variant="outlined"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      sx={{
                        bgcolor: "#F5F5F5",
                        border: errors.fullName && "1px solid red !important",
                        height: {
                          xs: "39px !important",
                          xl: "46px !important",
                        },
                        borderRadius: "8px !important",
                        fontWeight: 400,
                        fontSize: {
                          xs: "12px !important",
                          xl: "15px !important",
                        },
                        lineHeight: { xs: "", xl: "18.15px !important" },
                        p: {
                          xs: "5px 10px !important",
                          xl: "8px 12px 8px 16px !important",
                        },
                        "&:hover": {
                          border: "1px solid #2a9d8f !important",
                        },
                      }}
                    />
                    {errors.fullName && (
                      <span style={{ color: "red", fontSize: "0.7rem" }}>
                        {errors.fullName}
                      </span>
                    )}
                  </Box>
                  <Box sx={{ mb: 1, mt: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: { xs: 500 },
                        fontSize: "14px",
                        lineHeight: "24px",
                        color: "#292A2F",
                      }}
                    >
                      Email address <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <InputBase
                      fullWidth
                      placeholder="you@company.com"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      sx={{
                        bgcolor: "#F5F5F5",
                        border: errors.email && "1px solid red !important",
                        height: {
                          xs: "39px !important",
                          xl: "46px !important",
                        },
                        borderRadius: "8px !important",
                        fontWeight: 400,
                        fontSize: {
                          xs: "12px !important",
                          xl: "15px !important",
                        },
                        lineHeight: { xs: "", xl: "18.15px !important" },
                        p: {
                          xs: "5px 10px !important",
                          xl: "8px 12px 8px 16px !important",
                        },
                        "&:hover": {
                          border: "1px solid #2a9d8f !important",
                        },
                      }}
                    />
                    {errors.email && (
                      <span style={{ color: "red", fontSize: "0.7rem" }}>
                        {errors.email}
                      </span>
                    )}
                  </Box>
                  <Box sx={{ mb: 1, mt: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: { xs: 500 },
                        fontSize: "14px",
                        lineHeight: "24px",
                        color: "#292A2F",
                      }}
                    >
                      Phone number <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <PhoneInput
                      international
                      placeholder="000-0000"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      style={{
                        paddingLeft: "1rem !important",
                        borderRadius: "8px !important",
                        height: "42px !important",
                        color: "black !important",
                        background: "#F5F5F5",
                        marginBottom: errors.phone ? "" : "0.85rem !important",
                      }}
                      className="phone-input"
                      defaultCountry={country}
                    />
                    {errors.phone && (
                      <span style={{ color: "red", fontSize: "0.7rem" }}>
                        {errors.phone}
                      </span>
                    )}
                  </Box>
                  <Box sx={{ mb: 1, mt: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: { xs: 500 },
                        fontSize: "14px",
                        lineHeight: "24px",
                        color: "#292A2F",
                      }}
                    >
                      State
                    </Typography>
                    <FormControl fullWidth sx={{}}>
                      <Select
                        id="state"
                        name="state"
                        placeholder="Select your state"
                        value={formData.state}
                        onChange={handleInputChange}
                        error={!!errors.state}
                        sx={{
                          height: {
                            xs: "39px !important",
                            xl: "46px !important",
                          },
                          borderRadius: "8px",
                          fontWeight: 400,
                          bgcolor: "#F5F5F5 !important",
                          fontSize: {
                            xs: "12px !important",
                            xl: "15px !important",
                          },
                          lineHeight: { xs: "", xl: "18.15px !important" },
                          p: {
                            xs: "5px 0px !important",
                            xl: "8px 0px 8px 0px !important",
                          },
                          "&:hover": {
                            border: "none !important",
                            outline: "none !important",
                          },
                          "&.Mui-focused": {
                            border: "1px solid #2a9d8f !important",
                            outline: "none !important",
                          },
                          "& fieldset": {
                            border: "none !important",
                          },
                        }}
                      >
                        <MenuItem value="">Select your State</MenuItem>
                        {states?.length > 0
                          ? states?.map((state, index) => (
                              <MenuItem key={index} value={state?.name}>
                                {state?.name}
                              </MenuItem>
                            ))
                          : null}
                      </Select>
                      {errors.state && (
                        <span style={{ color: "red", fontSize: "0.7rem" }}>
                          {errors.state}
                        </span>
                      )}
                    </FormControl>
                  </Box>
                  <Box sx={{ mb: 1, mt: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: { xs: 500 },
                        fontSize: "14px",
                        lineHeight: "24px",
                        color: "#292A2F",
                      }}
                    >
                      Location
                    </Typography>
                    <InputBase
                      fullWidth
                      placeholder="California"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      sx={{
                        bgcolor: "#F5F5F5",
                        border: errors.location && "1px solid red !important",
                        height: {
                          xs: "39px !important",
                          xl: "46px !important",
                        },
                        borderRadius: "8px !important",
                        fontWeight: 400,
                        fontSize: {
                          xs: "12px !important",
                          xl: "15px !important",
                        },
                        lineHeight: { xs: "", xl: "18.15px !important" },
                        p: {
                          xs: "5px 10px !important",
                          xl: "8px 12px 8px 16px !important",
                        },
                        "&:hover": {
                          border: "1px solid #2a9d8f !important",
                        },
                      }}
                    />
                    {errors.location && (
                      <span style={{ color: "red", fontSize: "0.7rem" }}>
                        {errors.location}
                      </span>
                    )}
                  </Box>
                  <Box sx={{ mb: 1, mt: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: { xs: 500 },
                        fontSize: "14px",
                        lineHeight: "24px",
                        color: "#292A2F",
                      }}
                    >
                      Highest qualification{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth sx={{}}>
                      <Select
                        id="highest-qualification"
                        name="highestQualification"
                        placeholder="Select your qualification"
                        value={formData.highestQualification}
                        onChange={handleInputChange}
                        error={!!errors.highestQualification}
                        sx={{
                          height: { xs: "39px", xl: "46px" },
                          borderRadius: "8px",
                          fontWeight: 400,
                          bgcolor: "#F5F5F5",
                          fontSize: {
                            xs: "12px !important",
                            xl: "15px !important",
                          },
                          lineHeight: { xs: "", xl: "18.15px !important" },
                          p: {
                            xs: "5px 0px !important",
                            xl: "8px 0px 8px 0px !important",
                          },
                          "&:hover": {
                            border: "none !important",
                            outline: "none !important",
                          },
                          "&.Mui-focused": {
                            border: "1px solid #2a9d8f !important",
                            outline: "none !important",
                          },
                          "& fieldset": {
                            border: "none !important",
                          },
                        }}
                      >
                        <MenuItem value="">Select your qualification</MenuItem>
                        <MenuItem value="bachelor">Bachelor's degree</MenuItem>
                        <MenuItem value="master">Master's degree</MenuItem>
                        <MenuItem value="phd">Doctoral degree</MenuItem>
                      </Select>
                    </FormControl>
                    {errors.highestQualification && (
                      <span style={{ color: "red", fontSize: "0.7rem" }}>
                        {errors.highestQualification}
                      </span>
                    )}
                  </Box>
                  <Box sx={{ mb: 1, mt: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: { xs: 500 },
                        fontSize: "14px",
                        lineHeight: "24px",
                        color: "#292A2F",
                      }}
                    >
                      Year of experience <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <InputBase
                      fullWidth
                      placeholder="e.g, 3 Years"
                      name="yearsOfExperience"
                      value={formData.yearsOfExperience}
                      onChange={handleInputChange}
                      sx={{
                        bgcolor: "#F5F5F5",
                        border:
                          errors.yearsOfExperience &&
                          "1px solid red !important",
                        height: {
                          xs: "39px !important",
                          xl: "46px !important",
                        },
                        borderRadius: "8px !important",
                        fontWeight: 400,
                        fontSize: {
                          xs: "12px !important",
                          xl: "15px !important",
                        },
                        lineHeight: { xs: "", xl: "18.15px !important" },
                        p: {
                          xs: "5px 10px !important",
                          xl: "8px 12px 8px 16px !important",
                        },
                        "&:hover": {
                          border: "1px solid #2a9d8f !important",
                        },
                      }}
                    />
                    {errors.yearsOfExperience && (
                      <span style={{ color: "red", fontSize: "0.7rem" }}>
                        {errors.yearsOfExperience}
                      </span>
                    )}
                  </Box>
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: { xs: 500 },
                        fontSize: "14px",
                        lineHeight: "24px",
                        color: "#292A2F",
                      }}
                    >
                      Upload your resume (PDF/DOC/DOCX/JPEG){" "}
                      <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <InputBase
                      inputRef={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.jpeg"
                      onChange={handleFileChange}
                      sx={{
                        bgcolor: "#F5F5F5",
                        border: errors.resume && "1px solid red !important",
                        cursor: "pointer",
                        borderRadius: "8px !important",
                        fontWeight: 400,
                        fontSize: {
                          xs: "12px !important",
                          xl: "15px !important",
                        },
                        lineHeight: { xs: "", xl: "18.15px !important" },
                        p: {
                          xs: "5px 10px !important",
                          xl: "8px 12px 8px 16px !important",
                        },
                        "&:hover": {
                          border: "1px solid #2a9d8f !important",
                          cursor: "pointer",
                        },
                      }}
                    />
                    {errors.resume && (
                      <span style={{ color: "red", fontSize: "0.7rem" }}>
                        {errors.resume}
                      </span>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: { xs: 500 },
                      fontSize: "14px",
                      lineHeight: "24px",
                      color: "#292A2F",
                    }}
                  >
                    Cover letter
                  </Typography>
                  <InputBase
                    fullWidth
                    placeholder="Type here..."
                    name="coverLetter"
                    multiline
                    rows={6}
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    sx={{
                      bgcolor: "#F5F5F5 !important",
                      borderRadius: "8px !important",
                      fontWeight: 400,
                      fontSize: {
                        xs: "12px !important",
                        xl: "15px !important",
                      },
                      lineHeight: { xs: "", xl: "18.15px !important" },
                      p: {
                        xs: "5px 10px !important",
                        xl: "8px 12px 8px 16px !important",
                      },
                      "&:hover": {
                        border: "1px solid #2a9d8f !important",
                      },
                      "&.Mui-focused": {
                        outline: "none !important",
                        p: {
                          xs: "5px 10px !important",
                          xl: "8px 12px 8px 16px !important",
                        },
                      },
                    }}
                  />
                </>
              )}
              {showOTPField && (
                <Box
                  className="otp_input"
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: "#F8FAFC",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: "1px solid #E0E0E0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "16px", xl: "18px" },
                      color: "#002357",
                      mb: 1,
                    }}
                  >
                    Verify Your Email
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: "14px",
                      color: "#4B5EAA",
                      mb: 2,
                    }}
                  >
                    Enter the 6-digit code sent to your email
                  </Typography>
                  <OtpInput
                    value={otp}
                    onChange={setOtp}
                    numInputs={6}
                    renderInput={(props) => <input {...props} />}
                    inputStyle={{
                      width: "2.5rem",
                      height: "2.5rem",
                      margin: "0 0.3rem",
                      fontSize: "1.2rem",
                      borderRadius: "8px",
                      border: "1px solid #2a9d8f",
                      background: "#FFFFFF",
                      color: "#002357",
                      fontWeight: 500,
                      textAlign: "center",
                      "&:focus": {
                        border: "2px solid #2a9d8f",
                        outline: "none",
                      },
                    }}
                    containerStyle={{
                      justifyContent: "center",
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    {timeLeft > 0 ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#4B5EAA",
                          fontWeight: 500,
                          fontSize: "0.9rem",
                        }}
                      >
                        Time left: {formatTime(timeLeft)}
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#4B5EAA",
                          fontWeight: 500,
                          fontSize: "0.9rem",
                        }}
                      >
                        Didn't receive OTP?
                        <Button
                          onClick={resendOtp}
                          variant="text"
                          sx={{
                            textTransform: "none",
                            color: "#2a9d8f",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            ml: 1,
                            p: 0,
                            "&:hover": {
                              color: "#1f776e",
                              background: "none",
                            },
                          }}
                        >
                          Resend OTP
                        </Button>
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </form>
          </Box>
          <Box
            sx={{
              p: 2,
              textAlign: "left",
              minWidth: "193px !important",
              maxWidth: "250px !important",
              ml: { xl: 1.5 },
            }}
          >
            {showOTPField ? (
              otp && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={verifyOtp}
                  sx={{
                    textTransform: "none",
                    color: "#292A2F",
                    fontSize: {
                      xs: "0.875rem !important",
                      xl: "1rem !important",
                    },
                    fontWeight: { xs: 500, xl: 600 },
                    bgcolor: "#E9C369 !important",
                    borderRadius: "10px !important",
                    "&:hover": {
                      bgcolor: "#E9C369 !important",
                      color: "#292A2F !important",
                    },
                  }}
                >
                  {isLoading ? <CircularProgress size={19} /> : "Verify"}
                </Button>
              )
            ) : (
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                sx={{
                  textTransform: "none",
                  color: "#292A2F",
                  fontSize: {
                    xs: "0.875rem !important",
                    xl: "1rem !important",
                  },
                  fontWeight: { xs: 500, xl: 600 },
                  bgcolor: "#E9C369 !important",
                  borderRadius: "10px !important",
                  "&:hover": {
                    bgcolor: "#E9C369 !important",
                    color: "#292A2F !important",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={19} />
                ) : (
                  "Submit application"
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ApplyModal;
